import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Cloud,
  Database,
  ExternalLink,
  FileText,
  Globe,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Shield,
  StickyNote,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AutomatedSyncConsentText, FilePicker, SyncScheduleSection } from "@/components/data-sources";
import { PageHeader, useToast, VisibilityBadge, VisibilityTeamSelector } from "@/components/shared";
import { SharePointBrowser, type SharePointSelection } from "@/components/sharepoint/SharePointBrowser";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useMenuPosition } from "@/hooks/useMenuPosition";
import { useUpdateDataSource } from "@/http/mutations/data-source/useUpdateDataSource";
import { useUploadDataSourceFiles } from "@/http/mutations/data-source/useUploadDataSourceFiles";
import { getApiErrorMessage } from "@/lib/api";
import { dataSourceService } from "@/lib/services";
import type { DataCollection, DataSourceStatus, SyncSchedule, VisibilityType } from "@/types";

export function DataSources() {
  // NOSONAR -- page component with CRUD, polling, and form state
  const { teams } = useApp();
  const { profile } = useAuth();
  const { showToast } = useToast();
  const updateDataSource = useUpdateDataSource();
  const uploadFiles = useUploadDataSourceFiles();
  const navigate = useNavigate();
  const automatedSyncAvailable = Boolean(profile?.capabilities?.automatedSyncEnabled);
  const [dataSources, setDataSources] = useState<DataCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState<DataCollection | null>(null);
  const { position: menuPosition, open: openMenu, close: closeMenu } = useMenuPosition();
  const [createType, setCreateType] = useState<"upload" | "website" | "sharepoint" | "notes" | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DataCollection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create form state
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createWebsiteUrl, setCreateWebsiteUrl] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);
  const [sharepointSelection, setSharepointSelection] = useState<SharePointSelection | null>(null);
  const [sharepointShowBrowser, setSharepointShowBrowser] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [createVisibility, setCreateVisibility] = useState<VisibilityType>("private");
  const [createTeamUuid, setCreateTeamUuid] = useState<string>("");
  const [createNoteContent, setCreateNoteContent] = useState("");
  const [createAutoSync, setCreateAutoSync] = useState(false);
  const [createSyncSchedule, setCreateSyncSchedule] = useState<SyncSchedule | null>(null);

  // Default team for create form
  useEffect(() => {
    if (!createTeamUuid && teams.length > 0) {
      const defaultTeam = teams.find((t) => t.isEveryoneTeam) || teams[0];
      setCreateTeamUuid(defaultTeam.uuid);
    }
  }, [teams, createTeamUuid]);

  const ACTIVE_STATUSES = new Set(["pending", "syncing", "indexing"]);
  const POLL_INTERVAL_MS = 5000;

  const loadDataSources = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await dataSourceService.list();
      setDataSources(response.data);
    } catch (error) {
      console.error("Failed to load data sources:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDataSources();
  }, [loadDataSources]);

  // Poll for updates when any data source has an active status
  useEffect(() => {
    const hasActiveSource = dataSources.some((ds) => ACTIVE_STATUSES.has(ds.status));
    if (!hasActiveSource) return;

    const interval = setInterval(() => {
      loadDataSources(true);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dataSources, loadDataSources]);

  const handleCreateSource = async () => {
    // NOSONAR -- multi-step create flow with type-dependent logic
    if (!createName.trim() || !createTeamUuid || !createType) return;

    try {
      setIsCreating(true);

      const payload = buildCreatePayload();
      const response = await dataSourceService.create(payload);
      const newCollection = response.data;

      await enableAutomatedSyncAfterCreate(newCollection.uuid);
      await postCreateSync(newCollection.uuid);

      resetCreateForm();
      setShowCreateModal(false);
      setCreateType(null);

      await loadDataSources();
    } catch (error) {
      console.error("Failed to create data source:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const buildCreatePayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      name: createName,
      description: createDescription || undefined,
      source_type: createType,
      team_uuid: createTeamUuid,
      config: {},
      visibility: createVisibility,
    };

    if (createType === "notes" && createNoteContent) {
      payload.content = createNoteContent;
    }
    if (createType === "website" && createWebsiteUrl) {
      payload.config = { url: createWebsiteUrl };
    }
    if (createType === "sharepoint" && sharepointSelection) {
      const baseConfig: Record<string, unknown> = {
        sharepoint_site_url: sharepointSelection.site.web_url,
        sharepoint_site_id: sharepointSelection.site.id,
        sharepoint_drive_id: sharepointSelection.drive.id,
        sharepoint_drive_name: sharepointSelection.drive.name,
      };

      if (sharepointSelection.folderSelection) {
        // Folder-based selection
        const fs = sharepointSelection.folderSelection;
        baseConfig.sharepoint_folder_id = fs.folder.id;
        baseConfig.sharepoint_folder_name = fs.folder.name;
        baseConfig.sharepoint_folder_path = fs.folderPath;
        baseConfig.recursive = fs.recursive;
      } else {
        // Individual file selection
        baseConfig.selected_item_ids = sharepointSelection.items.map((i) => i.id);
        baseConfig.selected_item_names = sharepointSelection.items.map((i) => i.name);
      }

      payload.config = baseConfig;
    }

    return payload;
  };

  const enableAutomatedSyncAfterCreate = async (collectionUuid: string) => {
    if (createType !== "sharepoint" || !automatedSyncAvailable || !createAutoSync) return;
    try {
      await updateDataSource.mutateAsync({
        collectionUuid,
        data: { enableAutomatedSync: true, syncSchedule: createSyncSchedule },
      });
    } catch (error) {
      showToast(
        `Data source created, but automated sync could not be enabled: ${getApiErrorMessage(error) || "unknown error"}`,
        "error",
      );
    }
  };

  const postCreateSync = async (collectionUuid: string) => {
    if (createType === "upload" && selectedFiles.length > 0) {
      try {
        await uploadFiles.mutateAsync({ collectionUuid, files: selectedFiles });
      } catch (uploadError) {
        console.error("Failed to upload files:", uploadError);
        showToast(
          `Data source created, but files could not be uploaded: ${getApiErrorMessage(uploadError, "unknown error")}`,
          "error",
        );
      }
      return;
    }

    if (createType === "website" || createType === "sharepoint") {
      try {
        await dataSourceService.sync(collectionUuid);
      } catch (syncError) {
        console.error("Failed to trigger sync:", syncError);
      }
    }
  };

  const handleSync = async (collection: DataCollection) => {
    try {
      await dataSourceService.sync(collection.uuid);
      await loadDataSources();
    } catch (error) {
      console.error("Failed to sync:", error);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setIsDeleting(true);
      await dataSourceService.delete(deleteTarget.uuid);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await loadDataSources();
    } catch (error) {
      console.error("Failed to delete:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFilesChange = (accepted: File[], rejected: string[]) => {
    setSelectedFiles(accepted);
    setRejectedFiles(rejected);
  };

  const preventDefaultDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const resetCreateForm = () => {
    setCreateName("");
    setCreateDescription("");
    setCreateWebsiteUrl("");
    setSelectedFiles([]);
    setRejectedFiles([]);
    setSharepointSelection(null);
    setSharepointShowBrowser(true);
    setCreateVisibility("team");
    setCreateNoteContent("");
    setCreateAutoSync(false);
    setCreateSyncSchedule(null);
  };

  const filteredSources = dataSources.filter(
    (source) =>
      source.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "upload":
        return FileText;
      case "website":
        return Globe;
      case "sharepoint":
        return Cloud;
      case "notes":
        return StickyNote;
      default:
        return Database;
    }
  };

  const getStatusConfig = (status: DataSourceStatus) => {
    switch (status) {
      case "ready":
        return {
          icon: CheckCircle2,
          color: "text-green-400",
          bg: "bg-green-500/20",
          label: "Ready",
        };
      case "syncing":
        return {
          icon: RefreshCw,
          color: "text-blue-400",
          bg: "bg-blue-500/20",
          label: "Syncing",
          animate: true,
        };
      case "indexing":
        return {
          icon: RefreshCw,
          color: "text-blue-400",
          bg: "bg-blue-500/20",
          label: "Indexing",
          animate: true,
        };
      case "partial":
        return {
          icon: AlertCircle,
          color: "text-yellow-400",
          bg: "bg-yellow-500/20",
          label: "Partial",
        };
      case "error":
      case "failed":
        return {
          icon: AlertCircle,
          color: "text-red-400",
          bg: "bg-red-500/20",
          label: status === "failed" ? "Failed" : "Error",
        };
      case "pending":
        return {
          icon: Clock,
          color: "text-yellow-400",
          bg: "bg-yellow-500/20",
          label: "Pending",
        };
      default:
        return {
          icon: Clock,
          color: "text-content-tertiary",
          bg: "bg-surface-tertiary/20",
          label: "Unknown",
        };
    }
  };

  const sourceTypes: Array<{
    id: string;
    name: string;
    description: string;
    icon: typeof Upload;
    disabled?: boolean;
  }> = [
    {
      id: "upload",
      name: "File Upload",
      description: "Upload documents, PDFs, or text files",
      icon: Upload,
    },
    {
      id: "website",
      name: "Website",
      description: "Index a single web page or PDF",
      icon: Globe,
    },
    {
      id: "sharepoint",
      name: "SharePoint",
      description: "Connect to SharePoint document libraries",
      icon: Cloud,
    },
    {
      id: "notes",
      name: "Notes / Paste Text",
      description: "Paste text or notes to index as a document",
      icon: StickyNote,
    },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader
        icon={Database}
        title="Data Sources"
        subtitle="Manage your data collections for RAG-enabled agents"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            Add Data Source
          </button>
        }
      >
        {/* Search */}
        <div className="mt-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
          <input
            type="text"
            placeholder="Search data sources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
          />
        </div>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {(() => {
          if (loading) {
            return (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
              </div>
            );
          }
          if (filteredSources.length > 0) {
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSources.map((source) => {
                  const TypeIcon = getTypeIcon(source.sourceType);
                  const statusConfig = getStatusConfig(source.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <div
                      key={source.uuid}
                      role="button" // NOSONAR — contains nested interactive elements
                      tabIndex={0}
                      onClick={() => navigate(`/data-sources/${source.uuid}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/data-sources/${source.uuid}`);
                        }
                      }}
                      className="bg-surface-primary border border-border-primary rounded-lg p-4 hover:border-border-secondary transition-colors group cursor-pointer flex flex-col"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="h-10 w-10 rounded-lg bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center shrink-0">
                          <TypeIcon className="h-5 w-5 text-brand-primary" />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openMenu(e);
                            setSelectedSource(source);
                          }}
                          className="p-1.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </div>

                      <h3 className="font-medium text-content-primary truncate mb-1">{source.name}</h3>
                      <p className="text-sm text-content-tertiary line-clamp-2 mb-3 min-h-10">
                        {source.description || "\u00A0"}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-content-quaternary">
                        <span>{source.fileCount || 0} files</span>
                        <VisibilityBadge visibility={source.visibility || "team"} />
                        {source.automatedSyncEnabled && (
                          <span
                            title={
                              source.syncOwnerName
                                ? `Auto-syncs in the background (owner: ${source.syncOwnerName})`
                                : "Auto-syncs in the background"
                            }
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-primary/15 text-brand-primary font-medium"
                          >
                            <RefreshCw className="h-3 w-3" />
                            Auto-sync
                          </span>
                        )}
                      </div>

                      <div className="mt-auto pt-3 border-t border-border-primary flex items-center justify-between">
                        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${statusConfig.bg}`}>
                          <StatusIcon
                            className={`h-3 w-3 ${statusConfig.color} ${statusConfig.animate ? "animate-spin" : ""}`}
                          />
                          <span className={`text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
                        </div>
                        {source.lastSyncedAt && (
                          <span className="text-xs text-content-quaternary">
                            {new Date(source.lastSyncedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }
          return (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center mb-4">
                <Database className="h-8 w-8 text-brand-primary" />
              </div>
              <h3 className="text-lg font-medium text-content-primary">No data sources found</h3>
              <p className="text-content-tertiary mt-1 text-center max-w-sm">
                {searchQuery
                  ? "Try adjusting your search query"
                  : "Add documents, websites, or connect to SharePoint to enable RAG capabilities for your agents."}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  <Plus className="h-4 w-4" />
                  Add Data Source
                </button>
              )}
            </div>
          );
        })()}
      </div>

      {/* Create Modal - Type Selection */}
      {showCreateModal && !createType && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <h2 className="text-lg font-semibold text-content-primary">Add Data Source</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-content-tertiary text-sm mb-4">Choose how you want to add data to your collection</p>
              <div className="space-y-3">
                {sourceTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() =>
                        !type.disabled && setCreateType(type.id as "upload" | "website" | "sharepoint" | "notes")
                      }
                      disabled={type.disabled}
                      className="w-full flex items-center gap-4 p-4 bg-surface-secondary hover:bg-interactive-active border border-border-secondary hover:border-border-hover rounded-lg transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-interactive-hover"
                    >
                      <div className="h-10 w-10 rounded-lg bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-brand-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-content-primary">{type.name}</h3>
                        <p className="text-sm text-content-tertiary">{type.description}</p>
                        {type.disabled && <p className="text-xs text-content-quaternary mt-1">Coming soon</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal - Form */}
      {showCreateModal && createType && (
        <div
          className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4"
          onDragOver={preventDefaultDrop}
          onDrop={preventDefaultDrop}
        >
          <div
            className={`bg-surface-primary border border-border-primary rounded-xl w-full ${createType === "sharepoint" ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-hidden flex flex-col`}
          >
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setCreateType(null)}
                  className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
                <h2 className="text-lg font-semibold text-content-primary">
                  {createType === "upload" && "Upload Files"}
                  {createType === "website" && "Add Website"}
                  {createType === "sharepoint" && "Connect SharePoint"}
                  {createType === "notes" && "Add Notes"}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateType(null);
                  resetCreateForm();
                }}
                className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              <div>
                <label htmlFor="ds-collection-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Collection Name
                </label>
                <input
                  id="ds-collection-name"
                  type="text"
                  placeholder="e.g., Product Documentation"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                />
              </div>
              <div>
                <label htmlFor="ds-description" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Description
                </label>
                <textarea
                  id="ds-description"
                  rows={2}
                  placeholder="Describe this data source..."
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
                />
              </div>

              {/* Visibility & Team Selector */}
              <VisibilityTeamSelector
                visibility={createVisibility}
                teamUuid={createTeamUuid}
                onVisibilityChange={setCreateVisibility}
                onTeamChange={setCreateTeamUuid}
              />

              {createType === "upload" && (
                <div>
                  <label htmlFor="ds-file-upload" className="block text-sm font-medium text-content-secondary mb-1.5">
                    Files
                  </label>
                  <FilePicker
                    inputId="ds-file-upload"
                    selectedFiles={selectedFiles}
                    rejectedFiles={rejectedFiles}
                    onChange={handleFilesChange}
                  />
                </div>
              )}

              {createType === "website" && (
                <div>
                  <label htmlFor="ds-website-url" className="block text-sm font-medium text-content-secondary mb-1.5">
                    Website URL
                  </label>
                  <input
                    id="ds-website-url"
                    type="url"
                    placeholder="https://example.com or https://example.com/doc.pdf"
                    value={createWebsiteUrl}
                    onChange={(e) => setCreateWebsiteUrl(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                  />
                  <p className="text-xs text-content-quaternary mt-1.5">
                    We'll download the page content or PDF and index it
                  </p>
                </div>
              )}

              {createType === "sharepoint" && (
                <div className="border border-border-secondary rounded-lg overflow-hidden -mx-1">
                  {sharepointSelection && !sharepointShowBrowser ? (
                    <div className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-content-secondary">
                          {sharepointSelection.folderSelection ? (
                            <>
                              Folder{" "}
                              <span className="font-medium text-content-primary">
                                {sharepointSelection.folderSelection.folder.name}
                              </span>{" "}
                              ({sharepointSelection.folderSelection.fileCount} file
                              {sharepointSelection.folderSelection.fileCount === 1 ? "" : "s"}
                              {sharepointSelection.folderSelection.recursive ? ", including sub-folders" : ""}) from{" "}
                              <span className="text-content-primary">{sharepointSelection.drive.name}</span>
                            </>
                          ) : (
                            <>
                              <span className="font-medium text-content-primary">
                                {sharepointSelection.items.length}
                              </span>{" "}
                              file{sharepointSelection.items.length === 1 ? "" : "s"} selected from{" "}
                              <span className="text-content-primary">{sharepointSelection.drive.name}</span>
                            </>
                          )}
                        </p>
                        <button
                          onClick={() => {
                            setSharepointSelection(null);
                            setSharepointShowBrowser(true);
                          }}
                          className="text-xs text-brand-primary hover:text-brand-primary/80 transition-colors"
                        >
                          Change
                        </button>
                      </div>
                      {!sharepointSelection.folderSelection && (
                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                          {sharepointSelection.items.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-2 text-sm text-content-secondary bg-surface-secondary px-3 py-1.5 rounded-sm"
                            >
                              <FileText className="h-3.5 w-3.5 text-content-quaternary shrink-0" />
                              <span className="truncate">{item.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {sharepointSelection.folderSelection && (
                        <div className="text-xs text-content-quaternary bg-surface-secondary px-3 py-2 rounded-sm">
                          Path: {sharepointSelection.drive.name}/{sharepointSelection.folderSelection.folderPath}
                          {sharepointSelection.folderSelection.recursive && " (recursive)"}
                        </div>
                      )}
                    </div>
                  ) : (
                    <SharePointBrowser
                      onSelect={(selection) => {
                        setSharepointSelection(selection);
                        // Auto-populate name if empty
                        if (!createName.trim()) {
                          if (selection.folderSelection) {
                            setCreateName(`${selection.site.display_name} - ${selection.folderSelection.folder.name}`);
                          } else {
                            setCreateName(`${selection.site.display_name} - ${selection.drive.name}`);
                          }
                        }
                      }}
                      onClear={() => setSharepointSelection(null)}
                      onCancel={() => {
                        setCreateType(null);
                      }}
                      multiSelect
                      allowFolderSelect
                      title="Select SharePoint Files or Folder"
                      embedded
                    />
                  )}
                </div>
              )}

              {createType === "sharepoint" && automatedSyncAvailable && sharepointSelection && (
                <div className="px-4 py-3 bg-surface-secondary/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-2 min-w-0">
                      <Shield className="h-4 w-4 text-content-tertiary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-content-primary">Automated sync</p>
                        <p className="text-xs text-content-tertiary">
                          Keep this folder synced in the background using your Microsoft permissions.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={createAutoSync}
                      aria-label="Toggle automated sync"
                      onClick={() => setCreateAutoSync((on) => !on)}
                      disabled={isCreating}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                        createAutoSync ? "bg-brand-primary" : "bg-surface-tertiary"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          createAutoSync ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                  {createAutoSync && (
                    <>
                      <AutomatedSyncConsentText />
                      <p className="text-xs text-content-quaternary">
                        By creating this data source you authorize automated sync.
                      </p>
                      <SyncScheduleSection
                        schedule={createSyncSchedule}
                        onSave={setCreateSyncSchedule}
                        onRemove={() => setCreateSyncSchedule(null)}
                        disabled={isCreating}
                      />
                    </>
                  )}
                </div>
              )}

              {createType === "notes" && (
                <div>
                  <label htmlFor="ds-note-content" className="block text-sm font-medium text-content-secondary mb-1.5">
                    Content
                  </label>
                  <textarea
                    id="ds-note-content"
                    rows={8}
                    placeholder="Paste or type your text here. This will be saved as a markdown document and indexed for RAG."
                    value={createNoteContent}
                    onChange={(e) => setCreateNoteContent(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none font-mono text-sm"
                  />
                  <p className="text-xs text-content-quaternary mt-1.5">
                    Supports markdown formatting. The content will be saved as a .md file and indexed.
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateType(null);
                  resetCreateForm();
                }}
                disabled={isCreating}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSource}
                disabled={
                  isCreating ||
                  !createName.trim() ||
                  (createType === "upload" && selectedFiles.length === 0) ||
                  (createType === "website" && !createWebsiteUrl.trim()) ||
                  (createType === "sharepoint" && !sharepointSelection) ||
                  (createType === "sharepoint" &&
                    sharepointSelection &&
                    !sharepointSelection.folderSelection &&
                    sharepointSelection.items.length === 0) ||
                  (createType === "notes" && !createNoteContent.trim())
                }
                className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                {createType === "upload" && (isCreating ? "Creating..." : "Upload & Process")}
                {createType === "website" && (isCreating ? "Creating..." : "Add & Index")}
                {createType === "sharepoint" && (isCreating ? "Connecting..." : "Connect & Import")}
                {createType === "notes" && (isCreating ? "Saving..." : "Save & Index")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Source Actions Dropdown */}
      {selectedSource && menuPosition && (
        <div
          className="fixed inset-0 z-40"
          role="presentation" // NOSONAR — decorative backdrop element
          onClick={() => {
            setSelectedSource(null);
            closeMenu();
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setSelectedSource(null);
              closeMenu();
            }
          }}
        >
          <div // NOSONAR - context menu container, stopPropagation prevents backdrop dismiss
            style={{
              position: "fixed",
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
            className="bg-surface-primary border border-border-primary rounded-lg shadow-xl py-1 w-auto min-w-fit"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                handleSync(selectedSource);
                setSelectedSource(null);
                closeMenu();
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content-secondary hover:bg-interactive-hover transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Sync Now
            </button>
            <button
              onClick={() => {
                navigate(`/data-sources/${selectedSource.uuid}`);
                setSelectedSource(null);
                closeMenu();
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content-secondary hover:bg-interactive-hover transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              View Files
            </button>
            <div className="border-t border-border-primary my-1" />
            <button
              onClick={() => {
                setDeleteTarget(selectedSource);
                setShowDeleteModal(true);
                setSelectedSource(null);
                closeMenu();
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-interactive-hover transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <h2 className="text-lg font-semibold text-content-primary">Delete Data Source</h2>
              </div>
              <p className="text-content-tertiary mb-6">
                Are you sure you want to delete{" "}
                <span className="text-content-primary font-medium break-all line-clamp-2" title={deleteTarget.name}>
                  {deleteTarget.name}
                </span>
                ? This will remove all associated files and cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  disabled={isDeleting}
                  className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
